/**
 * useOnboardingGates — qué compuertas del onboarding siguen pendientes,
 * según el bloque `onboarding` de `/api/v1/me`.
 *
 * Hermano de `useAccountProvisioning`. Aquél responde "¿la cuenta ya opera?" y
 * trae UN siguiente paso, que es lo que necesita el cintillo: una sola
 * instrucción a la vez. Éste trae las banderas sueltas, porque un menú sí
 * puede listar lo que falta sin imponer un orden — el usuario elige por dónde
 * entrar.
 *
 * Igual que en `useAccountProvisioning`: sin dato resuelto se devuelve `null`
 * y quien consuma decide. No se asume "todo listo" ni "todo pendiente"
 * mientras la query vuela; inventar estado es cómo el cintillo terminó
 * anunciando trámites que no existían.
 */
import { useQuery } from '@tanstack/react-query';

import { clientApi } from '@tankef/api-client';
import type { OnboardingState } from '@tankef/core';

export interface OnboardingGatesResult {
  onboarding: OnboardingState | null;
  isLoading: boolean;
}

export function useOnboardingGates(enabled = true): OnboardingGatesResult {
  const query = useQuery({
    queryKey: ['me', 'onboarding-gates'],
    queryFn: () => clientApi.meOnboarding(),
    enabled,
    // Mismo criterio que `useAccountProvisioning`: cambia cuando el usuario
    // avanza en su expediente, no cada segundo.
    staleTime: 60_000,
  });

  return {
    onboarding: query.data ?? null,
    isLoading: query.isLoading,
  };
}
